import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Play, Timer, ShieldCheck, PenTool, Workflow,
  Search, PencilRuler, Cog, Factory, Truck, Wrench, ClipboardCheck, KeyRound,
  Home as HomeIcon, Building2, Rocket, CircleDot, Store, Palmtree, Leaf, Check, PencilLine,
} from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp, MaskLines, easeLux } from "../components/motion";
import { fetchCategories } from "../lib/api";
import { BUILD_WAYS, PRODUCT_CATEGORIES } from "../data/catalog";

const CAT_ICONS = {
  "prefab-homes": HomeIcon,
  "precast-villas": Building2,
  "space-capsules": Rocket,
  "apple-pods": CircleDot,
  "commercial-pods": Store,
  "hospitality-resort": Palmtree,
};

const FEATURES = [
  { Icon: Timer, title: "Fast Delivery", desc: "Up to 45% faster vs. traditional construction" },
  { Icon: ShieldCheck, title: "Premium Materials", desc: "Engineered for durability and elegance" },
  { Icon: PenTool, title: "Design Flexibility", desc: "Fully customizable to your vision" },
  { Icon: Workflow, title: "End-to-End Execution", desc: "From concept to completion, seamlessly" },
];

const PROCESS = [
  { n: "01", Icon: Search, name: "Discover", desc: "Understand your vision & needs" },
  { n: "02", Icon: PencilRuler, name: "Design", desc: "Conceptualize & plan" },
  { n: "03", Icon: Cog, name: "Engineer", desc: "Precision engineering" },
  { n: "04", Icon: Factory, name: "Manufacture", desc: "Factory-built with quality control" },
  { n: "05", Icon: Truck, name: "Deliver", desc: "Safe & timely transport" },
  { n: "06", Icon: Wrench, name: "Install", desc: "On-site quick installation" },
  { n: "07", Icon: ClipboardCheck, name: "Test & Check", desc: "Quality & safety assurance" },
  { n: "08", Icon: KeyRound, name: "Handover", desc: "Ready for you to live / use" },
];

const MARQUEE = ["Prefab Homes", "Precast Villas", "Space Capsules", "Apple Pods", "Commercial Pods", "Hospitality & Resort"];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [slide, setSlide] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % PRODUCT_CATEGORIES.length), 4500);
    return () => clearInterval(t);
  }, []);

  const activeCat = PRODUCT_CATEGORIES[slide];

  return (
    <PageWrapper>
      {/* ================= HERO ================= */}
      <section ref={heroRef} className="relative pt-[110px] lg:pt-[130px] pb-14 lg:pb-20 overflow-hidden grain" data-testid="home-hero">
        <div className="absolute top-[-200px] right-[-200px] w-[560px] h-[560px] rounded-full bg-[#D9A441]/[0.06] blur-[120px] pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left */}
          <div className="lg:col-span-5 relative z-10">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: easeLux }}
              className="eyebrow eyebrow-dash text-[#D9A441] mb-6"
            >
              Built Smart. Designed to Inspire.
            </motion.div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3]" data-testid="hero-title">
              <MaskLines lines={["Luxury Modular", "Living, Reimagined."]} delay={0.15} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: easeLux }}
              className="mt-6 text-sm md:text-base text-[#F2EDE3]/60 leading-relaxed max-w-md"
            >
              NUVUN delivers premium prefab, precast and space solutions with
              unmatched design, speed, and sustainability.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: easeLux }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/contact" data-testid="hero-start-project" className="btn-gold">
                Start Your Project
              </Link>
              <a href="#solutions" data-testid="hero-explore-solutions" className="btn-ghost">
                Explore Solutions
                <span className="w-6 h-6 rounded-full border border-[#D9A441]/50 flex items-center justify-center">
                  <Play className="w-2.5 h-2.5 fill-current" />
                </span>
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
              data-testid="hero-badges"
            >
              {[
                { Icon: Cog, t: "Precision Built." },
                { Icon: Truck, t: "Delivered Fast." },
                { Icon: ShieldCheck, t: "Built to Last." },
              ].map(({ Icon, t }, i) => (
                <span key={t} className="flex items-center gap-2.5 text-xs text-[#F2EDE3]/65">
                  <Icon className="w-4 h-4 text-[#D9A441]" />
                  {t}
                  {i < 2 && <span className="ml-3 w-px h-4 bg-[#D9A441]/25 hidden sm:block" />}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right — hero slideshow (6 product types) with parallax */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.35, ease: easeLux }}
            className="lg:col-span-7 relative"
          >
            <div className="relative overflow-hidden rounded-lg gold-frame shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]" data-testid="hero-slideshow">
              <div className="relative w-full h-[320px] sm:h-[420px] lg:h-[540px]">
                <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
                  <AnimatePresence mode="sync">
                    <motion.img
                      key={activeCat.slug}
                      src={activeCat.hero_image}
                      alt={activeCat.name}
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.2, ease: easeLux }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </AnimatePresence>
                </motion.div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-5 right-5 w-11 h-11 rounded-full border border-[#D9A441]/60 bg-[#0A0908]/60 backdrop-blur-md flex items-center justify-center">
                <img src="/images/nuvun/logo-icon.png" alt="" className="w-6 h-6 object-contain" />
              </div>
              {/* Slide caption */}
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeCat.slug}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.5, ease: easeLux }}
                    className="bg-[#0A0908]/65 backdrop-blur-md border border-[#D9A441]/30 rounded-md px-4 py-3"
                    data-testid="hero-slide-caption"
                  >
                    <div className="eyebrow !text-[9px] text-[#D9A441]">{activeCat.num} — Collection</div>
                    <div className="font-display text-lg md:text-xl text-[#F2EDE3] mt-0.5">{activeCat.name}</div>
                  </motion.div>
                </AnimatePresence>
                <div className="flex items-center gap-2 pb-1" data-testid="hero-slide-dots">
                  {PRODUCT_CATEGORIES.map((c, i) => (
                    <button
                      key={c.slug}
                      onClick={() => setSlide(i)}
                      aria-label={`Show ${c.name}`}
                      data-testid={`hero-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        i === slide ? "w-7 bg-[#D9A441]" : "w-1.5 bg-white/35 hover:bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= EDITORIAL MARQUEE ================= */}
      <div className="border-y border-[#D9A441]/15 py-5 overflow-hidden bg-[#0C0B09]" data-testid="home-marquee">
        <div className="marquee-track">
          {[0, 1].map((rep) => (
            <div key={rep} className="flex items-center shrink-0">
              {MARQUEE.map((t) => (
                <span key={`${rep}-${t}`} className="flex items-center">
                  <span className="font-display italic text-xl md:text-2xl text-[#F2EDE3]/70 px-8 whitespace-nowrap">{t}</span>
                  <span className="text-[#D9A441] text-sm">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ================= OUR MODULAR SOLUTIONS ================= */}
      <section id="solutions" className="py-20 lg:py-28" data-testid="home-solutions">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp className="text-center max-w-2xl mx-auto">
            <div className="eyebrow text-[#D9A441] mb-4">Our Modular Solutions</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F2EDE3]">
              Spaces for every vision. <span className="italic text-[#F2EDE3]/70">Built for every tomorrow.</span>
            </h2>
          </FadeUp>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {categories.map((c, i) => {
              const Icon = CAT_ICONS[c.slug] || HomeIcon;
              return (
                <FadeUp key={c.slug} delay={i * 0.07}>
                  <Link
                    to={`/products/category/${c.slug}`}
                    data-testid={`solution-card-${c.slug}`}
                    className="group block panel overflow-hidden hover:border-[#D9A441]/50 transition-colors duration-500 h-full"
                  >
                    <div className="flex items-center justify-between px-5 pt-5">
                      <span className="w-9 h-9 rounded-sm border border-[#D9A441]/40 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#D9A441]" />
                      </span>
                      <span className="font-condensed text-[#D9A441]/70 text-lg">{c.num}</span>
                    </div>
                    <div className="px-5 pt-4">
                      <div className="relative overflow-hidden rounded-md aspect-[4/5]">
                        <img
                          src={c.hero_image}
                          alt={c.name}
                          loading="lazy"
                          className="product-card-img absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/50 to-transparent" />
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="font-body text-[13px] font-extrabold tracking-[0.08em] uppercase text-[#F2EDE3]">{c.name}</div>
                      <div className="text-xs text-[#F2EDE3]/50 mt-1.5 leading-relaxed">{c.tagline}</div>
                      <ArrowRight className="w-4 h-4 text-[#D9A441] mt-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </div>
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FEATURE STRIP ================= */}
      <section className="pb-20 lg:pb-24" data-testid="home-features">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <FadeUp className="panel grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 divide-[#D9A441]/10 lg:divide-x">
            {FEATURES.map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4 p-7">
                <span className="w-11 h-11 rounded-full border border-[#D9A441]/45 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#D9A441]" />
                </span>
                <span>
                  <span className="block eyebrow text-[#F2EDE3] !tracking-[0.14em]">{title}</span>
                  <span className="block text-xs text-[#F2EDE3]/50 mt-2 leading-relaxed">{desc}</span>
                </span>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* ================= OUR PROCESS ================= */}
      <section className="pb-20 lg:pb-28" data-testid="home-process">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="panel relative overflow-hidden px-6 md:px-10 lg:px-14 py-12 lg:py-16">
            <span className="watermark absolute right-4 top-2 text-[180px] md:text-[280px]" aria-hidden>08</span>
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <FadeUp>
                  <div className="eyebrow text-[#D9A441] mb-4">Our Process</div>
                  <h2 className="font-display text-3xl sm:text-4xl text-[#F2EDE3] leading-[1.15]">
                    From idea to handover,<br />
                    in <span className="text-[#D9A441] italic">8 simple steps.</span>
                  </h2>
                  <p className="mt-5 text-sm text-[#F2EDE3]/55 leading-relaxed max-w-sm">
                    A transparent, technology-driven process that ensures precision,
                    speed and peace of mind.
                  </p>
                  <Link to="/about#process" data-testid="process-how-it-works" className="btn-gold mt-8">
                    How It Works
                  </Link>
                </FadeUp>
              </div>
              <div className="lg:col-span-8">
                <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-y-10 gap-x-4">
                  {PROCESS.map(({ n, Icon, name, desc }, i) => (
                    <FadeUp key={n} delay={i * 0.06} className="relative text-center px-1">
                      <div className="relative mx-auto w-14 h-14 rounded-full border border-[#D9A441]/40 bg-[#0A0908] flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#D9A441]" />
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#D9A441] text-[#0A0908] text-[9px] font-extrabold flex items-center justify-center">
                          {i + 1}
                        </span>
                      </div>
                      <div className="mt-3 font-condensed text-[#D9A441]/80 text-sm">{n}</div>
                      <div className="eyebrow !tracking-[0.12em] text-[#F2EDE3] mt-1">{name}</div>
                      <div className="text-[11px] text-[#F2EDE3]/45 mt-1.5 leading-snug">{desc}</div>
                    </FadeUp>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= BUILD YOUR WAY (light editorial) ================= */}
      <section className="bg-[#F2EDE3] text-[#141210] py-20 lg:py-28 relative" data-testid="home-build-your-way">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <FadeUp>
                <div className="eyebrow text-[#9C7420] mb-4">Build Your Way</div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#141210] leading-[1.12]">
                  One platform.<br />Eight ways to build.
                </h2>
              </FadeUp>
              <FadeUp delay={0.15} className="relative mt-10">
                <div className="overflow-hidden rounded-lg">
                  <img
                    src="/images/gen/aframe-forest.jpg"
                    alt="A-frame cabin glowing in a forest"
                    loading="lazy"
                    className="w-full h-[300px] md:h-[380px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 right-6 md:-right-6 bg-gradient-to-br from-[#E2AA35] to-[#D9A441] text-[#141210] px-6 py-5 rounded-md shadow-xl max-w-[200px]">
                  <div className="font-condensed text-4xl">60–90%</div>
                  <div className="eyebrow !text-[9px] mt-1">Faster than traditional construction</div>
                </div>
              </FadeUp>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-10 content-start">
              {BUILD_WAYS.map((w, i) => (
                <FadeUp key={w.n} delay={i * 0.05}>
                  <Link
                    to={w.to}
                    data-testid={`build-way-${w.n}`}
                    className="group flex items-center gap-5 py-5 border-b border-[#141210]/12 hover:border-[#D9A441] transition-colors"
                  >
                    <span className="font-condensed text-[#B98A2B] text-lg w-8 shrink-0">{w.n}</span>
                    <span className="flex-1">
                      <span className="block font-body font-extrabold text-[13px] tracking-[0.1em] uppercase text-[#141210]">{w.name}</span>
                      <span className="block text-xs text-[#141210]/55 mt-1">{w.desc}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#B98A2B] transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>

          {/* ---- Prefab made better ---- */}
          <div className="mt-20 lg:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <FadeUp className="lg:col-span-5">
              <div className="overflow-hidden rounded-lg">
                <img
                  src="/images/gen/prefab-better.jpg"
                  alt="NUVUN wellness retreat pavilion"
                  loading="lazy"
                  className="w-full h-[300px] md:h-[420px] object-cover"
                />
              </div>
            </FadeUp>
            <div className="lg:col-span-4">
              <FadeUp delay={0.1}>
                <div className="eyebrow text-[#9C7420] mb-4">Prefab Made Better</div>
                <h2 className="font-display text-3xl sm:text-4xl text-[#141210] leading-[1.12]">
                  Comparable.<br />Configurable &amp;<br />Order-ready.
                </h2>
                <p className="mt-5 text-sm text-[#141210]/60 leading-relaxed">
                  Precision-engineered for quality, speed, and sustainability. Our systems
                  are built to adapt, scale, and exceed expectations — from homes to hospitality.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/products" data-testid="explore-products-cta" className="btn-gold !shadow-none">
                    Explore Products <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <Link to="/resources" data-testid="view-catalogue-cta" className="btn-ghost !border-[#141210]/30 !text-[#141210] hover:!bg-[#141210]/5">
                    View Catalogue
                  </Link>
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={0.2} className="lg:col-span-3">
              <div className="bg-[#141210] text-[#F2EDE3] rounded-lg p-8 relative overflow-hidden">
                <Leaf className="absolute -bottom-6 -right-6 w-32 h-32 text-[#D9A441]/10" />
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-9 h-9 rounded-full border border-[#D9A441]/50 flex items-center justify-center">
                    <Leaf className="w-4 h-4 text-[#D9A441]" />
                  </span>
                  <span className="font-display text-lg">Sustainable by Design</span>
                </div>
                <ul className="space-y-3.5">
                  {["Lower carbon footprint", "Efficient resource use", "Long-lasting performance", "Built for a better future"].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm text-[#F2EDE3]/75">
                      <Check className="w-4 h-4 text-[#D9A441] shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ================= CTA BAND ================= */}
      <section className="border-b border-[#D9A441]/15" data-testid="home-cta-band">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 py-14 lg:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <FadeUp className="flex items-start gap-6">
            <span className="hidden sm:flex w-14 h-14 rounded-md border border-[#D9A441]/45 items-center justify-center shrink-0">
              <PencilLine className="w-6 h-6 text-[#D9A441]" />
            </span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#F2EDE3] leading-[1.2]">
              Build resort inventory.<br />
              <span className="italic text-[#F2EDE3]/70">The way modern brands build product.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link to="/hospitality" data-testid="cta-build-together" className="btn-gold whitespace-nowrap">
              Let&apos;s Build Together <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
